import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

// Definieer de structuur van onze context data
interface CompanyContextType {
  user: User | null;
  companyId: string | undefined;
  isLoading: boolean;
}

// Creëer de context met een standaardwaarde
const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// Dit is de Provider component die onze app zal "inpakken"
export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  // Functie om company_id op te halen uit de database
  const fetchCompanyIdFromDatabase = async (userId: string) => {
    try {
      console.log('Ophalen company_id uit database voor user:', userId);
      
      const { data, error } = await supabase
        .from('employees')
        .select('company_id')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Fout bij ophalen company_id uit database:', error);
        return null;
      }

      if (data?.company_id) {
        console.log('Company_id succesvol opgehaald uit database:', data.company_id);
        return data.company_id;
      } else {
        console.warn('Geen company_id gevonden in database voor user:', userId);
        return null;
      }
    } catch (error) {
      console.error('Onverwachte fout bij database lookup:', error);
      return null;
    }
  };

  useEffect(() => {
    // Deze functie handelt zowel de initiële sessie als updates af.
    const setSessionData = async (session: Session | null) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        // EERSTE POGING: Probeer uit app_metadata (voor backward compatibility)
        const metadataCompanyId = currentUser.app_metadata?.company_id;
        
        if (metadataCompanyId) {
          console.log('Company_id gevonden in metadata:', metadataCompanyId);
          setCompanyId(metadataCompanyId);
          setIsLoading(false);
          return;
        }

        // TWEEDE POGING: Haal op uit database (nieuwe aanpak)
        console.log('Metadata bevat geen company_id, zoeken in database...');
        const databaseCompanyId = await fetchCompanyIdFromDatabase(currentUser.id);
        
        if (databaseCompanyId) {
          setCompanyId(databaseCompanyId);
        } else {
          console.error(
            "FATALE FOUT: company_id niet gevonden in metadata noch in database. Onboarding is mogelijk mislukt voor deze gebruiker."
          );
          setCompanyId(undefined);
        }
      } else {
        setCompanyId(undefined); // Geen gebruiker, dus geen ID.
      }
      
      setIsLoading(false);
    };

    // Haal de huidige sessie op bij de start
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSessionData(session);
    });

    // Luister naar veranderingen in authenticatie status
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionData(session);
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const value = { user, companyId, isLoading };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

// Dit is de custom hook die onze pagina's gaan gebruiken
export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};